import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import axios from 'axios';
import { Plus, Edit, Trash2, User, Phone, Mail, MapPin, FileText, Wallet } from 'lucide-react';
import { CustomerStatementModal } from '../components/CustomerStatementModal';
import { CustomerNfeButton } from '../components/NfeActions';

import { useListKeyboardNav } from '../lib/useListKeyboardNav';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Capitaliza cada palavra: "joão da silva" → "João Da Silva"
const titleCase = (s) => (s || '')
  .toLowerCase()
  .replace(/(^|\s|['-])(\p{L})/gu, (m) => m.toUpperCase());

const EMPTY = {
  name: '', cpf_cnpj: '', cnpj: '', state_registration: '', phone: '', email: '', address: '', address_number: '', neighborhood: '', ibge_code: '',
  city: '', state: '', zip_code: '', credit_limit: '', credit_blocked: false, notes: ''
};

export const CustomersPage = () => {
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [statementCustomerId, setStatementCustomerId] = useState(null);
  const [openingRows, setOpeningRows] = useState([]);
  const [existingOpenings, setExistingOpenings] = useState([]);

  useEffect(() => { loadCustomers(); }, []);

  const today = new Date().toISOString().slice(0, 10);

  const loadOpenings = async (customerId) => {
    try {
      const { data } = await axios.get(`${API}/customers/${customerId}/opening-balances`, { withCredentials: true });
      setExistingOpenings(data.items || []);
    } catch (err) { setExistingOpenings([]); }
  };

  const addOpeningRow = () => setOpeningRows([...openingRows, { amount: '', original_date: today, notes: '' }]);
  const updateOpeningRow = (i, field, value) => {
    const rows = [...openingRows];
    rows[i] = { ...rows[i], [field]: value };
    setOpeningRows(rows);
  };
  const removeOpeningRow = (i) => setOpeningRows(openingRows.filter((_, idx) => idx !== i));

  const deleteExistingOpening = async (id) => {
    if (!window.confirm('Excluir este saldo de abertura?')) return;
    try {
      await axios.delete(`${API}/customers/${editingId}/opening-balances/${id}`, { withCredentials: true });
      await loadOpenings(editingId);
      loadCustomers();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao excluir saldo de abertura');
    }
  };

  const postOpenings = async (customerId) => {
    const entries = openingRows
      .filter((r) => parseFloat(r.amount) > 0 && r.original_date)
      .map((r) => ({ amount: parseFloat(r.amount), original_date: r.original_date, notes: r.notes || null }));
    if (entries.length === 0) return;
    await axios.post(`${API}/customers/${customerId}/opening-balances`, { entries }, { withCredentials: true });
  };

  const loadCustomers = async () => {
    try {
      const { data } = await axios.get(`${API}/customers`, { withCredentials: true });
      setCustomers(data);
    } catch (err) { console.error(err); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const payload = {
        ...formData,
        name: titleCase(formData.name),
        city: titleCase(formData.city),
        address: titleCase(formData.address),
        credit_limit: parseFloat(formData.credit_limit) || 0
      };
      if (editingId) {
        await axios.put(`${API}/customers/${editingId}`, payload, { withCredentials: true });
        await postOpenings(editingId);
      } else {
        const { data } = await axios.post(`${API}/customers`, payload, { withCredentials: true });
        await postOpenings(data.id);
      }
      loadCustomers();
      resetForm();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao salvar cliente');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (c) => {
    setFormData({
      name: c.name || '', cpf_cnpj: c.cpf_cnpj || '', cnpj: c.cnpj || '', state_registration: c.state_registration || '', phone: c.phone || '',
      email: c.email || '', address: c.address || '', city: c.city || '',
      state: c.state || '', zip_code: c.zip_code || '', address_number: c.address_number || '', neighborhood: c.neighborhood || '', ibge_code: c.ibge_code || '',
      credit_limit: (c.credit_limit || 0).toString(), credit_blocked: !!c.credit_blocked, notes: c.notes || ''
    });
    setEditingId(c.id);
    setShowForm(true);
    setOpeningRows([]);
    loadOpenings(c.id);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir este cliente?')) return;
    try {
      await axios.delete(`${API}/customers/${id}`, { withCredentials: true });
      loadCustomers();
    } catch (err) { setError('Erro ao excluir'); }
  };

  const resetForm = () => {
    setFormData(EMPTY);
    setEditingId(null);
    setShowForm(false);
    setError('');
    setOpeningRows([]);
    setExistingOpenings([]);
  };

  const filtered = customers.filter(c =>
    !search || c.name.toLowerCase().includes(search.toLowerCase()) ||
    (c.cpf_cnpj && c.cpf_cnpj.includes(search)) ||
    (c.phone && c.phone.includes(search))
  );
  const nav = useListKeyboardNav({ count: filtered.length, resetKey: search, onSelect: (i) => setStatementCustomerId(filtered[i].id) });

  return (
    <Layout>
      <div className="p-8" data-testid="customers-page">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-4xl font-black text-slate-900 tracking-tight">Clientes</h1>
          <Button onClick={() => setShowForm(!showForm)} className="bg-blue-600 hover:bg-blue-700" data-testid="add-customer-btn">
            <Plus size={20} className="mr-2" /> Novo Cliente
          </Button>
        </div>

        <Input
          placeholder="Buscar por nome, CPF/CNPJ ou telefone..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={nav.onKeyDown}
          title="↑ ↓ navegam na lista · Enter abre o extrato"
          className="mb-6"
          data-testid="search-customers"
        />

        {showForm && (
          <div className="bg-white rounded-md border border-slate-200 p-6 mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">{editingId ? 'Editar' : 'Novo'} Cliente</h2>
            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2 md:col-span-2">
                <Label>Nome *</Label>
                <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required data-testid="customer-name-input" />
              </div>
              <div className="space-y-2">
                <Label>CPF / CNPJ</Label>
                <Input value={formData.cpf_cnpj} onChange={(e) => setFormData({ ...formData, cpf_cnpj: e.target.value })} placeholder="000.000.000-00" data-testid="customer-cpf-cnpj-input" />
              </div>
              <div className="space-y-2">
                <Label>CNPJ</Label>
                <Input value={formData.cnpj} onChange={(e) => setFormData({ ...formData, cnpj: e.target.value })} placeholder="00.000.000/0000-00" data-testid="customer-cnpj-input" />
              </div>
              <div className="space-y-2">
                <Label>Inscrição Estadual</Label>
                <Input value={formData.state_registration} onChange={(e) => setFormData({ ...formData, state_registration: e.target.value })} placeholder="Isento ou número da IE" data-testid="customer-ie-input" />
              </div>
              <div className="space-y-2">
                <Label>Telefone</Label>
                <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="(00) 00000-0000" />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Email</Label>
                <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Endereço</Label>
                <Input value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} placeholder="Rua, número, bairro" />
              </div>
              <div className="space-y-2">
                <Label>Número</Label>
                <Input value={formData.address_number} onChange={(e) => setFormData({ ...formData, address_number: e.target.value })} placeholder="S/N" data-testid="customer-address-number-input" />
              </div>
              <div className="space-y-2">
                <Label>Bairro</Label>
                <Input value={formData.neighborhood} onChange={(e) => setFormData({ ...formData, neighborhood: e.target.value })} data-testid="customer-neighborhood-input" />
              </div>
              <div className="space-y-2">
                <Label>Cód. IBGE do município</Label>
                <Input value={formData.ibge_code} onChange={(e) => setFormData({ ...formData, ibge_code: e.target.value })} placeholder="ex.: 5002704 (para NF-e)" maxLength={7} data-testid="customer-ibge-input" />
              </div>
              <div className="space-y-2">
                <Label>Cidade</Label>
                <Input value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Estado</Label>
                <Input value={formData.state} onChange={(e) => setFormData({ ...formData, state: e.target.value })} placeholder="UF" maxLength={2} />
              </div>
              <div className="space-y-2">
                <Label>CEP</Label>
                <Input value={formData.zip_code} onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })} placeholder="00000-000" />
              </div>
              <div className="space-y-2">
                <Label>Limite de Crédito (R$)</Label>
                <Input type="number" step="0.01" value={formData.credit_limit} onChange={(e) => setFormData({ ...formData, credit_limit: e.target.value })} placeholder="0.00" />
                <p className="text-[11px] text-slate-400">0 = sem limite (ilimitado)</p>
              </div>
              <div className="space-y-2 md:col-span-2 flex items-start">
                <label className="flex items-start gap-2 cursor-pointer mt-6 select-none" data-testid="credit-blocked-toggle">
                  <input
                    type="checkbox"
                    checked={!!formData.credit_blocked}
                    onChange={(e) => setFormData({ ...formData, credit_blocked: e.target.checked })}
                    className="mt-0.5 h-4 w-4 accent-red-600"
                    data-testid="credit-blocked-checkbox"
                  />
                  <span>
                    <span className="text-sm font-bold text-red-700">Bloquear vendas a prazo (SEM FIADO)</span>
                    <span className="block text-[11px] text-slate-400">Trava 100% das vendas no crediário para este cliente, mesmo dentro do limite.</span>
                  </span>
                </label>
              </div>
              <div className="space-y-2 md:col-span-3">
                <Label>Observações</Label>
                <Input value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} />
              </div>
              {error && <div className="md:col-span-3"><Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert></div>}

              <div className="md:col-span-3 border-t border-slate-200 pt-4 mt-2" data-testid="opening-balances-section">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <Wallet size={18} className="text-amber-600" />
                    <h3 className="text-base font-bold text-slate-900">Saldos de Abertura (notas antigas)</h3>
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={addOpeningRow} data-testid="add-opening-row-btn">
                    <Plus size={16} className="mr-1" /> Adicionar nota
                  </Button>
                </div>
                <p className="text-[11px] text-slate-400 mb-3">
                  Lance dívidas antigas do sistema anterior. Elas entram no crediário como pendentes,
                  com a <b>data original</b> da nota, identificadas como "saldo de abertura" (não são vendas novas).
                </p>

                {existingOpenings.length > 0 && (
                  <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 divide-y divide-amber-100" data-testid="existing-openings-list">
                    {existingOpenings.map((o) => (
                      <div key={o.id} className="flex items-center justify-between px-3 py-2 text-sm">
                        <div>
                          <span className="font-semibold text-amber-800">R$ {o.amount.toFixed(2)}</span>
                          <span className="text-slate-500 ml-2">{o.original_date ? new Date(o.original_date).toLocaleDateString('pt-BR') : ''}</span>
                          {o.notes && <span className="text-slate-400 ml-2">· {o.notes}</span>}
                          {o.paid && <span className="ml-2 text-emerald-600 font-medium">(pago)</span>}
                        </div>
                        {!o.paid && (
                          <button type="button" onClick={() => deleteExistingOpening(o.id)} className="text-red-600 hover:text-red-700" data-testid={`delete-opening-${o.id}`}>
                            <Trash2 size={15} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {openingRows.map((row, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 mb-2 items-end" data-testid={`opening-row-${i}`}>
                    <div className="col-span-3 space-y-1">
                      <Label className="text-xs">Valor (R$)</Label>
                      <Input type="number" step="0.01" value={row.amount} onChange={(e) => updateOpeningRow(i, 'amount', e.target.value)} placeholder="0.00" data-testid={`opening-amount-${i}`} />
                    </div>
                    <div className="col-span-3 space-y-1">
                      <Label className="text-xs">Data original</Label>
                      <Input type="date" max={today} value={row.original_date} onChange={(e) => updateOpeningRow(i, 'original_date', e.target.value)} data-testid={`opening-date-${i}`} />
                    </div>
                    <div className="col-span-5 space-y-1">
                      <Label className="text-xs">Observação (opcional)</Label>
                      <Input value={row.notes} onChange={(e) => updateOpeningRow(i, 'notes', e.target.value)} placeholder="Ex.: Nota 123 / fiado antigo" data-testid={`opening-notes-${i}`} />
                    </div>
                    <div className="col-span-1">
                      <button type="button" onClick={() => removeOpeningRow(i)} className="text-red-600 hover:text-red-700 p-2" data-testid={`remove-opening-row-${i}`}>
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}
                {openingRows.length === 0 && existingOpenings.length === 0 && (
                  <p className="text-xs text-slate-400 italic">Nenhuma nota antiga lançada. Clique em "Adicionar nota" para migrar saldos.</p>
                )}
              </div>

              <div className="md:col-span-3 flex gap-2">
                <Button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700" data-testid="save-customer-btn">{loading ? 'Salvando...' : 'Salvar'}</Button>
                <Button type="button" variant="outline" onClick={resetForm} data-testid="cancel-customer-btn">Cancelar</Button>
              </div>
            </form>
          </div>
        )}

        <div className="bg-white rounded-md border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Cliente</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Documento</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Contato</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Cidade/UF</th>
                  <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Limite</th>
                  <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Saldo</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {filtered.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-slate-400">Nenhum cliente cadastrado</td></tr>}
                {filtered.map((c, idx) => (
                  <tr key={c.id} {...nav.rowProps(idx, 'hover:bg-slate-50')}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <User size={18} className="text-slate-400" />
                        <span className="font-medium text-slate-900">{c.name}</span>
                        {c.credit_blocked && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-700 border border-red-200" data-testid={`no-credit-badge-${c.id}`}>SEM FIADO</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      <div>{c.cpf_cnpj || c.cnpj || '-'}</div>
                      {c.state_registration && <div className="text-xs text-slate-400">IE: {c.state_registration}</div>}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {c.phone && <div className="flex items-center gap-1 text-slate-600"><Phone size={12} />{c.phone}</div>}
                      {c.email && <div className="flex items-center gap-1 text-slate-500 text-xs"><Mail size={12} />{c.email}</div>}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {c.city ? `${c.city}${c.state ? '/' + c.state : ''}` : '-'}
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-medium text-blue-600">R$ {(c.credit_limit || 0).toFixed(2)}</td>
                    <td className="px-4 py-3 text-right text-sm font-medium">
                      <span className={c.balance > 0 ? 'text-red-600' : 'text-slate-600'}>
                        R$ {(c.balance || 0).toFixed(2)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button onClick={() => setStatementCustomerId(c.id)} className="text-emerald-600 hover:text-emerald-700 flex items-center gap-1 text-sm font-medium" data-testid={`statement-btn-${c.id}`} title="Ver extrato de venda a prazo">
                          <FileText size={16} /> Extrato
                        </button>
                        <CustomerNfeButton customer={c} />
                        <button onClick={() => handleEdit(c)} className="text-blue-600 hover:text-blue-700" data-testid={`edit-customer-${c.id}`}><Edit size={16} /></button>
                        <button onClick={() => handleDelete(c.id)} className="text-red-600 hover:text-red-700" data-testid={`delete-customer-${c.id}`}><Trash2 size={16} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {statementCustomerId && (
        <CustomerStatementModal customerId={statementCustomerId} onClose={() => setStatementCustomerId(null)} />
      )}
    </Layout>
  );
};
